import type { ClientCatalogProps } from "@core/SitePresenter/SitePresenter";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import { IGNORED_MARKS } from "@ext/git/core/Diff/logic/canonicalChanges";
import type { DiffExtensionProps } from "@ext/git/core/Diff/logic/DiffExtension";
import DiffLinesExtension from "@ext/git/core/Diff/logic/DiffExtension";
import { getReactNodeRenderers } from "@ext/git/core/Diff/logic/getReactNodeRenderers";
import normalizeForDiff from "@ext/git/core/Diff/logic/normalizeForDiff";
import { useSetting } from "@ext/settings/logic/hooks";
import { DiffExtension, type DiffOptions } from "@gaurussel/tiptap-diff-utility";
import type { Editor, Extensions, JSONContent } from "@tiptap/core";
import { useCallback, useEffect, useMemo, useRef } from "react";

interface GetDiffExtensionsProps extends Partial<DiffExtensionProps> {
	oldContent?: JSONContent;
	enabled?: boolean;
	oldApiUrlCreator?: ApiUrlCreator;
	oldCatalogProps?: ClientCatalogProps;
	isOldContextLoading?: boolean;
}

interface UseDiffExtensionsResult {
	extensions: Extensions;
	onEditorCreate: (editor: Editor) => void;
	/** The only safe way to update options: the library merges each patch with the mount-time options, so a raw partial `setDiffOptions` resets the rest. */
	updateDiffOptions: (patch: Partial<DiffOptions>) => void;
}

export const useDiffExtensions = ({
	oldContent,
	enabled = true,
	isLeftPinned,
	oldScope,
	newScope,
	articlePath,
	oldApiUrlCreator,
	oldCatalogProps,
	isOldContextLoading,
	...diffProps
}: GetDiffExtensionsProps): UseDiffExtensionsResult => {
	const oldContext = useMemo(
		() =>
			oldApiUrlCreator && oldCatalogProps
				? { apiUrlCreator: oldApiUrlCreator, catalogProps: oldCatalogProps }
				: undefined,
		[oldApiUrlCreator, oldCatalogProps],
	);
	const editorRef = useRef<Editor | null>(null);

	const [sensitivity] = useSetting("contentCompare.sensitivity");
	const [minSimilarity] = useSetting("contentCompare.minSimilarity");
	const [debounce] = useSetting("contentCompare.debounce");
	const [minMatchLength] = useSetting("contentCompare.minMatchLength");
	const [stopWordMinBlocks] = useSetting("contentCompare.stopWordMinBlocks");
	const [cutDownMinWords] = useSetting("contentCompare.cutDownMinWords");
	const [cutDownMinKept] = useSetting("contentCompare.cutDownMinKept");

	const isLoading = !!isOldContextLoading;

	const optionsRef = useRef<DiffOptions>(null);
	if (!optionsRef.current) {
		optionsRef.current = {
			enabled,
			baseline: oldContent,
			sensitivity,
			minSimilarity,
			debounceMs: debounce,
			minMatchLength,
			stopWordMinBlocks,
			cutDownMinWords,
			cutDownMinKept,
			ignoreMarks: IGNORED_MARKS,
			normalize: normalizeForDiff,
			react: getReactNodeRenderers({ oldContext, isLoading }),
		};
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: contents are read on recompute, but must not trigger one — that would reset the baseline on every edit
	const diffExtensions = useMemo(
		() => [
			DiffLinesExtension.configure({
				...diffProps,
				isLeftPinned,
				oldScope,
				newScope,
				articlePath,
			}),
			DiffExtension.configure(optionsRef.current),
		],
		[enabled, isLeftPinned, oldScope, newScope, articlePath],
	);

	const updateDiffOptions = useCallback((patch: Partial<DiffOptions>) => {
		optionsRef.current = { ...optionsRef.current, ...patch };
		const editor = editorRef.current;
		if (!editor || editor.isDestroyed) return;
		editor.commands.setDiffOptions(optionsRef.current);
		// The library reads `baseline` from options only at plugin creation; a live instance needs the command.
		if ("baseline" in patch && patch.baseline) editor.commands.setDiffBaseline(patch.baseline);
	}, []);

	const onEditorCreate = useCallback((editor: Editor) => {
		editorRef.current = editor;
	}, []);

	useEffect(() => {
		updateDiffOptions({
			sensitivity,
			minSimilarity,
			debounceMs: debounce,
			minMatchLength,
			stopWordMinBlocks,
			cutDownMinWords,
			cutDownMinKept,
		});
	}, [
		debounce,
		minMatchLength,
		minSimilarity,
		sensitivity,
		stopWordMinBlocks,
		cutDownMinWords,
		cutDownMinKept,
		updateDiffOptions,
	]);

	// Kept apart from numeric settings: `LiveDiff` sets the old-revision context and a settings change must not overwrite it.
	useEffect(() => {
		updateDiffOptions({ react: getReactNodeRenderers({ oldContext, isLoading }) });
	}, [updateDiffOptions, oldContext, isLoading]);

	return { extensions: diffExtensions, onEditorCreate, updateDiffOptions };
};
